import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, RefreshCw, Send, Sparkles, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import DailyBriefingTile from "../../components/admin/DailyBriefingTile";

const PRIORITY_STYLES = {
  urgent: "bg-rose-500/10 text-rose-300 border-rose-400/30",
  high: "bg-amber-400/10 text-amber-200 border-amber-400/25",
  normal: "bg-white/[0.04] text-zinc-300 border-white/[0.08]",
  low: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
};

const TYPE_LABEL = {
  standup: "Daily standup",
  escalation: "Escalation",
  proposal: "Proposal",
  status_update: "Status",
  reply: "Reply",
  question_to_ops: "Question",
  // Brief 3.4.5 — milestone outbound types
  milestone_skipped_no_binding: "Milestone · no Telegram handle",
  milestone_manual_send_required: "Milestone · manual send",
};

export default function AdminOpsInbox() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [askMode, setAskMode] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [todayDigest, setTodayDigest] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data }, digestResp] = await Promise.all([
          api.get("/admin/ops/inbox?limit=80"),
          api.get("/admin/ops/digest/today").catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setMessages(data?.messages || []);
          setTodayDigest(digestResp?.data?.digest || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post("/admin/ops/messages", { content: draft, is_question: askMode });
      setDraft("");
      setReloadTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const regenerateDigest = async () => {
    try {
      const { data } = await api.post("/admin/ops/digest/regenerate");
      setTodayDigest(data.digest);
      setReloadTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Regen failed");
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <Inbox className="h-3 w-3 text-[#00E5FF]" /> Ops
      </div>
      <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
        Inbox
      </h1>
      <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed max-w-2xl">
        Nerea's daily standup, escalations, and proposals. Reply or ask Nerea a question below.
      </p>

      {/* Today's briefing (Brief 3.6 Item 4 — structured 3-section view,
          replaces the LLM-text-blob standup) */}
      <div className="mt-7">
        <DailyBriefingTile />
      </div>

      {/* Compose */}
      <div className="mt-7 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">Compose</div>
          <label className="flex items-center gap-2 text-[12px] text-zinc-400">
            <input
              type="checkbox"
              data-testid="ops-ask-toggle"
              checked={askMode}
              onChange={(e) => setAskMode(e.target.checked)}
              className="h-3.5 w-3.5 accent-[#00E5FF]"
            />
            Ask Nerea (vs plain reply)
          </label>
        </div>
        <textarea
          data-testid="ops-compose-textarea"
          rows={3}
          placeholder={askMode ? "Ask Nerea a question…" : "Type a reply…"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13.5px] text-white focus:outline-none focus:border-[#00E5FF]/40 resize-none"
        />
        <div className="mt-2 flex justify-end">
          <button
            data-testid="ops-send-btn"
            onClick={send}
            disabled={sending || !draft.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 border border-[#00E5FF]/30 text-[#00E5FF] px-3 py-1.5 text-[12.5px] transition-colors disabled:opacity-40"
          >
            <Send className="h-3.5 w-3.5" />
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>

      {/* Feed */}
      <div className="mt-7 space-y-3" data-testid="ops-inbox-feed">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">— Feed</div>
        {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
        {!loading && messages.length === 0 && (
          <div className="rounded-xl border border-dashed border-white/[0.08] p-5 text-[13px] text-zinc-500" data-testid="ops-inbox-empty">
            No messages yet.
          </div>
        )}
        {messages.map((m) => (
          <div key={m.message_id} className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4" data-testid={`ops-msg-${m.message_id}`}>
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em]">
              <span className={`px-2 py-0.5 rounded-md border ${PRIORITY_STYLES[m.priority] || PRIORITY_STYLES.normal}`}>
                {m.priority}
              </span>
              <span className="text-zinc-500">{TYPE_LABEL[m.message_type] || m.message_type}</span>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-500">
                {m.author === "nerea" ? "Nerea" : "Ops"}
              </span>
              <span className="text-zinc-500 ml-auto">{(m.created_at || "").slice(0, 19).replace("T", " ")}</span>
            </div>
            <div className="mt-2 whitespace-pre-wrap text-[13.5px] text-zinc-200 leading-relaxed">
              {m.content}
            </div>
            {m.metadata?.deeplink ? (
              <Link
                to={m.metadata.deeplink}
                data-testid={`ops-msg-deeplink-${m.message_id}`}
                className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-cyan-300 hover:text-cyan-200"
              >
                <ExternalLink className="h-3 w-3" /> Open merchant
              </Link>
            ) : null}
            {m.citations?.length ? (
              <div className="mt-2 text-[11px] text-zinc-500">
                Cites: {m.citations.map((c) => `${c.source}/${c.source_title}`).join(", ")}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
